import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { LangSwitcher } from "@/components/site/LangSwitcher";
import { LOGO_URL } from "@/lib/api";

const links = [
  { to: "/", key: "nav.home" },
  { to: "/gallery", key: "nav.gallery" },
  { to: "/rooms", key: "nav.rooms" },
  { to: "/experience", key: "nav.experience" },
  { to: "/booking", key: "nav.booking" },
  { to: "/contact", key: "nav.contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 text-accent ${
        scrolled
          ? "py-2 bg-foreground/85 backdrop-blur-xl border-b border-accent/10"
          : "py-3 bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center group">
          <img
            src={LOGO_URL}
            alt="La dimora di Patty"
            className="h-24 sm:h-28 lg:h-32 w-auto object-contain transition-transform duration-500 group-hover:scale-105"
            style={{ mixBlendMode: "screen", filter: "brightness(1.15) contrast(1.1) drop-shadow(0 2px 12px rgba(0,0,0,0.4))" }}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.key.replace("nav.", "")}`}
              end={l.to === "/"}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors hover:text-[#5B6E7F] ${
                  isActive ? "text-[#5B6E7F]" : "text-accent"
                }`
              }
            >
              {t(l.key)}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <LangSwitcher variant="dark" />
          <div className="w-px h-6 bg-accent/30" />
          {user ? (
            <>
              {(user.role === "manager" || user.role === "admin") && (
                <button
                  data-testid="nav-dashboard"
                  onClick={() => navigate("/dashboard")}
                  className="text-sm flex items-center gap-2 px-4 py-2 border rounded-full hover:border-[#5B6E7F] transition-colors border-accent/50 text-accent hover:text-[#5B6E7F]"
                >
                  <LayoutDashboard size={14} /> {t("nav.dashboard")}
                </button>
              )}
              <button
                data-testid="nav-logout"
                onClick={() => {
                  logout();
                  navigate("/");
                }}
                className="text-sm flex items-center gap-2 hover:text-[#5B6E7F] text-accent"
              >
                <LogOut size={14} />
                {t("nav.logout")}
              </button>
            </>
          ) : (
            <Link
              to="/login"
              data-testid="nav-login"
              className="text-sm hover:text-[#5B6E7F] text-accent"
            >
              {t("nav.login")}
            </Link>
          )}
          <Link
            to="/booking"
            data-testid="nav-book-now"
            className="btn-shimmer text-sm px-6 py-2.5 rounded-full bg-accent text-accent-foreground hover:bg-white hover:text-foreground transition-colors"
          >
            {t("nav.book_now")}
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden p-2"
          onClick={() => setOpen((s) => !s)}
          aria-label="menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden glass mt-3 mx-4 rounded-lg p-6 animate-fade-up">
          <div className="flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `text-base ${isActive ? "text-accent" : "text-foreground"}`
                }
              >
                {t(l.key)}
              </NavLink>
            ))}
            <div className="h-px bg-border my-2" />
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground uppercase tracking-widest">Language</span>
              <LangSwitcher variant="light" />
            </div>
            <div className="h-px bg-border my-2" />
            {user ? (
              <>
                {(user.role === "manager" || user.role === "admin") && (
                  <Link to="/dashboard" onClick={() => setOpen(false)} className="text-base">
                    {t("nav.dashboard")}
                  </Link>
                )}
                <button
                  onClick={() => {
                    logout();
                    setOpen(false);
                    navigate("/");
                  }}
                  className="text-left text-base text-muted-foreground"
                >
                  {t("nav.logout")}
                </button>
              </>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="text-base">
                {t("nav.login")}
              </Link>
            )}
            <Link
              to="/booking"
              onClick={() => setOpen(false)}
              className="mt-2 text-center px-6 py-3 rounded-full bg-foreground text-background"
            >
              {t("nav.book_now")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
